import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Mail, Lock, User, ArrowRight, Loader2 } from 'lucide-react';
import { api } from './convex/_generated/api';
import { useAction } from "convex/react";
import { toast } from 'sonner';

export default function Auth2Meal() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const createUser = useAction(api.mfa.createUsers);
  const loginUser = useAction(api.mfa.loginUser);
  const sendOTP = useAction(api.mfa.sendOTP);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isLogin) {
        const user = await loginUser({ email, password });
        if (!user) {
          toast.error("User not found");
        } else {
          toast.success(`Welcome back, ${user.fullName || user.email}`);
          if (!user.mfaEnabled) {
            navigate("/mfa-setup", { state: { userId: user.userId, email: user.email } });
          } else {
            await sendOTP({ userId: user.userId, email: user.email });
            navigate("/mfa-verify", { state: { userId: user.userId, email: user.email } });
          }
        }
      } else {
        if (!fullName.trim()) {
          toast.error("Please enter your full name");
          return;
        }
        const user = await createUser({ email, password, fullName });
        toast.success("Account created!");
        if (!user.mfaEnabled) {
          navigate("/mfa-setup", { state: { userId: user.userId, email: user.email } });
        } else {
          navigate("/mfa-verify", { state: { userId: user.userId, email: user.email } });
        }
      }
    } catch (error) {
      toast.error(error.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth2-page">
      <style>{`
        .auth2-page {
          width: 100%;
          min-height: 100vh;
          background: #0a0a0a;
          font-family: 'Inter', sans-serif;
          position: relative;
          overflow-x: hidden;
        }
        .auth2-glow1 {
          position: fixed;
          top: -100px;
          right: -100px;
          width: 400px;
          height: 400px;
          background: rgba(0, 230, 118, 0.12);
          border-radius: 50%;
          filter: blur(120px);
          pointer-events: none;
        }
        .auth2-glow2 {
          position: fixed;
          bottom: -100px;
          left: -100px;
          width: 400px;
          height: 400px;
          background: rgba(0, 230, 118, 0.08);
          border-radius: 50%;
          filter: blur(120px);
          pointer-events: none;
        }
        .auth2-header {
          border-bottom: 1px solid #2a2a2a;
          padding: 0 24px;
          height: 60px;
          display: flex;
          align-items: center;
          position: sticky;
          top: 0;
          background: rgba(10, 10, 10, 0.96);
          backdrop-filter: blur(16px);
          z-index: 100;
        }
        .auth2-header-inner {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .auth2-logo-emoji {
          font-size: 20px;
        }
        .auth2-logo-text {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 22px;
          font-weight: 700;
          color: #f4f4f2;
        }
        .auth2-logo-beta {
          font-size: 10px;
          font-weight: 600;
          color: #00e676;
          border: 1px solid #00e676;
          border-radius: 4px;
          padding: 2px 6px;
          letter-spacing: 0.08em;
          font-family: 'IBM Plex Mono', monospace;
        }
        .auth2-center {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: calc(100vh - 60px);
          padding: 20px;
          position: relative;
          z-index: 1;
        }
        .auth2-card {
          background: #151515;
          border: 1px solid #2a2a2a;
          border-radius: 24px;
          padding: 40px 36px;
          width: 100%;
          max-width: 420px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
        }
        .auth2-icon-wrap {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          background: #00e676;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 20px;
          box-shadow: 0 8px 24px rgba(0, 230, 118, 0.3);
        }
        .auth2-title {
          font-family: 'Space Grotesk', sans-serif;
          font-size: 26px;
          font-weight: 700;
          color: #f4f4f2;
          text-align: center;
          margin: 0 0 8px;
          letter-spacing: -0.5px;
        }
        .auth2-subtitle {
          font-size: 14px;
          color: #9a9a95;
          text-align: center;
          line-height: 1.6;
          margin: 0 0 28px;
        }
        .auth2-form {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
        .auth2-field-wrap {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .auth2-label {
          font-size: 13px;
          font-weight: 600;
          color: #9a9a95;
        }
        .auth2-input-wrap {
          position: relative;
        }
        .auth2-input-icon {
          position: absolute;
          top: 50%;
          left: 12px;
          transform: translateY(-50%);
          color: #6b6b68;
          pointer-events: none;
        }
        .auth2-input {
          width: 100%;
          background: #1c1c1c;
          border: 1px solid #2a2a2a;
          border-radius: 12px;
          padding: 12px 12px 12px 38px;
          color: #f4f4f2;
          font-size: 14px;
          font-family: 'Inter', sans-serif;
          outline: none;
          box-sizing: border-box;
          transition: border-color 0.2s, background 0.2s;
        }
        .auth2-input::placeholder {
          color: #6b6b68;
        }
        .auth2-input:focus {
          border-color: #00e676;
          background: #202020;
        }
        .auth2-btn {
          width: 100%;
          background: #00e676;
          color: #04160d;
          border: none;
          border-radius: 12px;
          padding: 14px 20px;
          font-size: 15px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 8px;
          transition: opacity 0.2s, transform 0.1s;
        }
        .auth2-btn:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }
        .auth2-toggle-wrap {
          margin-top: 20px;
          text-align: center;
        }
        .auth2-toggle-btn {
          background: none;
          border: none;
          color: #9a9a95;
          font-size: 13px;
          cursor: pointer;
          font-family: 'Inter', sans-serif;
          transition: color 0.2s;
        }
        .auth2-toggle-btn:hover {
          color: #00e676;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .auth2-spinner {
          animation: spin 1s linear infinite;
        }
        @media (max-width: 480px) {
          .auth2-card {
            padding: 32px 22px;
            border-radius: 18px;
          }
          .auth2-title {
            font-size: 22px;
          }
        }
      `}</style>

      <div className="auth2-glow1" />
      <div className="auth2-glow2" />

      {/* Header */}
      <header className="auth2-header">
        <div className="auth2-header-inner">
          <span className="auth2-logo-emoji">⚡</span>
          <span className="auth2-logo-text">Meal</span>
          <span className="auth2-logo-beta">Lets Go!</span>
        </div>
      </header>

      <div className="auth2-center">
        <div className="auth2-card">

          {/* Icon */}
          <div className="auth2-icon-wrap">
            <Zap size={28} color="#04160d" />
          </div>

          <h1 className="auth2-title">
            {isLogin ? 'Welcome back' : 'Create account'}
          </h1>
          <p className="auth2-subtitle">
            {isLogin
              ? 'Sign in to access your scripts'
              : 'Start saying what needs to be said'}
          </p>

          <form onSubmit={handleSubmit} className="auth2-form">

            {!isLogin && (
              <div className="auth2-field-wrap">
                <label className="auth2-label">Full Name</label>
                <div className="auth2-input-wrap">
                  <User size={16} className="auth2-input-icon" />
                  <input
                    type="text"
                    placeholder="John Doe"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="auth2-input"
                    required
                  />
                </div>
              </div>
            )}

            <div className="auth2-field-wrap">
              <label className="auth2-label">Email</label>
              <div className="auth2-input-wrap">
                <Mail size={16} className="auth2-input-icon" />
                <input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="auth2-input"
                  required
                />
              </div>
            </div>

            <div className="auth2-field-wrap">
              <label className="auth2-label">Password</label>
              <div className="auth2-input-wrap">
                <Lock size={16} className="auth2-input-icon" />
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="auth2-input"
                  required
                  minLength={8}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="auth2-btn"
              style={{ opacity: loading ? 0.7 : 1, cursor: loading ? "not-allowed" : "pointer" }}
            >
              {loading ? (
                <Loader2 size={18} className="auth2-spinner" />
              ) : (
                <>
                  {isLogin ? 'Sign In' : 'Create Account'}
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div className="auth2-toggle-wrap">
            <button
              type="button"
              onClick={() => setIsLogin(!isLogin)}
              className="auth2-toggle-btn"
            >
              {isLogin
                ? "Don't have an account? Sign up"
                : "Already have an account? Sign in"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}